import { useId, useState, type ComponentProps, type FocusEvent } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { STATUS_BORDER, STATUS_LABEL, type FloatingLabelInputStatus } from '@/components/ui/floating-label-input';

export interface FloatingLabelSelectProps extends Omit<ComponentProps<'select'>, 'size'> {
    /** Shown as the empty option while nothing is chosen; floats onto the top border on focus / once a value is chosen. */
    label: string;
    status?: FloatingLabelInputStatus;
    /** Classes for the bordered box. */
    containerClassName?: string;
    labelClassName?: string;
}

/**
 * Native <select> with the same look and behaviour as FloatingLabelInput: the label rests inside the box as the
 * empty option, and floats onto the top border when the select is focused or has a value.
 *
 * A select has no :placeholder-shown, so "floated" is tracked from focus + value. The empty option is rendered
 * here (blank while floated, so the label text isn't shown twice); pass only the real options as children.
 *
 * Uses the same sizing variables as FloatingLabelInput (--fl-h, --fl-py, --fl-px), with the padding on the <select>.
 */
export function FloatingLabelSelect({
    label,
    status = 'default',
    id,
    value,
    className,
    containerClassName,
    labelClassName,
    children,
    onFocus,
    onBlur,
    ...props
}: FloatingLabelSelectProps) {
    const generatedId = useId();
    const selectId = id ?? generatedId;
    const [focused, setFocused] = useState(false);
    const hasValue = value != null && value !== '';
    const floated = focused || hasValue;

    return (
        <div
            data-slot="floating-label-select"
            className={cn(
                'group relative flex h-[var(--fl-h,62px)] w-full items-center rounded border border-solid bg-white transition-colors',
                STATUS_BORDER[status],
                status === 'default' && 'has-[select[aria-invalid=true]]:border-destructive',
                containerClassName
            )}>
            <label
                htmlFor={selectId}
                className={cn(
                    'font-lato pointer-events-none absolute -top-2 left-3 z-10 bg-white px-1 text-xs leading-4 font-normal',
                    'transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
                    floated ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0',
                    STATUS_LABEL[status],
                    status === 'default' && 'group-has-[select[aria-invalid=true]]:text-destructive',
                    labelClassName
                )}>
                {label}
            </label>

            <select
                id={selectId}
                value={value ?? ''}
                onFocus={(e: FocusEvent<HTMLSelectElement>) => {
                    setFocused(true);
                    onFocus?.(e);
                }}
                onBlur={(e: FocusEvent<HTMLSelectElement>) => {
                    setFocused(false);
                    onBlur?.(e);
                }}
                className={cn(
                    'font-lato h-full w-full min-w-0 cursor-pointer appearance-none bg-transparent py-[var(--fl-py,20px)] pr-9 pl-[var(--fl-px,8.5px)] text-base leading-[22px] font-normal tracking-normal outline-none',
                    'disabled:cursor-not-allowed',
                    hasValue ? 'text-[#17171B]' : 'text-[#7B7B7B]',
                    className
                )}
                {...props}>
                <option value="" className="text-[#7B7B7B]">
                    {floated ? '' : label}
                </option>
                {children}
            </select>

            <ChevronDown
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 right-[var(--fl-px,8.5px)] size-5 -translate-y-1/2 text-[#555555]"
            />
        </div>
    );
}
